import { Popover, PopoverProps, message } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useMemoizedFn, useUnmount, useDebounceEffect } from 'ahooks';
import { useSearchPhraseV2 } from 'views/Setting/Phrase/hooks/useSearch';
import PhraseItem, { PhraseItemRef, getPhraseTextElement } from './NewPhraseItem';
import { PhraseToolbar } from './NewPhraseToolbar';
import { usePhraseDisplayMode } from 'hooks/business/usePhraseDisplayMode';
import { getFillPhraseText, sortPhraseList } from './utils';
import { useTranslate } from '@tolgee/react';
import { useSetConversationToggle } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { PhraseValue } from 'hooks/business/usePhraseOnChat';
import { invert } from 'lodash-es';
import { PhrasesTextElement } from 'services/api/phrase';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';

const PopoverContent = styled.div`
  margin: -12px -16px;
  > div {
    position: relative;
    max-height: 260px;
    padding: 4px;
    overflow: auto;
  }
  > footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 14px;
    box-shadow: 0 -4px 8px 0 #0000001a;
  }
`;

const countLimit = 20;

export interface PassivePopoverProps extends PopoverProps {
  width?: number;
  phraseList?: PhraseValue[];
  // 是否启动联想
  isRelateRegion: boolean;
  region: General.UpperCaseRegion;
  visible?: boolean;
  mercadoQAmode?: boolean;
  conversationToggle?: boolean;
  input: string;
  setInput: (input: string) => void;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  onFillPhraseText: (data: string) => void;
  onContentClick?: () => void;
}

function NewPassivePopover(props: PassivePopoverProps) {
  const { phraseList, isRelateRegion, children, ...otherProps } = props;

  if (!isRelateRegion || !phraseList || phraseList?.length === 0) {
    return <>{children}</>;
  }

  return (
    <Implement phraseList={phraseList} {...otherProps}>
      {children}
    </Implement>
  );
}

interface ImplementProps extends PopoverProps {
  width?: number;
  phraseList?: PhraseValue[];
  region: General.UpperCaseRegion;
  visible?: boolean;
  mercadoQAmode?: boolean;
  conversationToggle?: boolean;
  input: string;
  setInput: (input: string) => void;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  onFillPhraseText: (data: string) => void;
  onContentClick?: () => void;
}

interface MatchedPhraseListProps {
  indexEnable?: boolean;
  width?: number;
  mercadoQAmode?: boolean;
  list: PhraseValue[];
  region: General.UpperCaseRegion;
  selectIndex: number;
  indexMap: Record<number, number>;
  setIndexMap: Dispatch<SetStateAction<Record<number, number>>>;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  setSelectIndex: (index: number) => void;
  setInput: (val: string) => void;
  onFillPhraseText: (data: string) => void;
}

function MatchedPhraseList(props: MatchedPhraseListProps) {
  const {
    indexEnable,
    width,
    mercadoQAmode,
    list,
    selectIndex,
    indexMap,
    setIndexMap,
    onPressEnter,
    onPhraseSelected,
    setSelectIndex,
    setInput,
    onFillPhraseText,
  } = props;
  const { mode } = usePhraseDisplayMode();
  const { t } = useTranslate();

  const itemsRef = useRef<Array<PhraseItemRef | null>>([]);

  const handleSendClick = useMemoizedFn((data: PhraseValue) => {
    onPressEnter?.(data);
    setSelectIndex(-1);
    setInput('');
  });

  const handleEditClick = useMemoizedFn((data: PhrasesTextElement) => {
    setSelectIndex(-1);
    onFillPhraseText?.(getFillPhraseText(data, mode !== 'onlyFianl'));
  });

  const scrollTo = useMemoizedFn((index: number) => {
    if (itemsRef.current[index]) {
      itemsRef.current[index]?.scrollTo();
    }
  });

  useUnmount(() => {
    onPhraseSelected?.(undefined);
  });

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!indexEnable || !el) {
      return () => {
        setIndexMap({});
      };
    }

    const handler = () => {
      const { scrollTop, clientHeight } = el;
      let index = 0;
      const indexMap: Record<number, number> = {};
      [...el.children].some((_child, i) => {
        const child = _child as HTMLDivElement;
        if (child.offsetTop > scrollTop + clientHeight) {
          return true;
        }
        if (child.offsetTop + child.clientHeight > scrollTop) {
          index++;
          indexMap[i] = index;
        }
        return false;
      });
      setIndexMap(indexMap);
    };

    setTimeout(() => {
      handler();
    });

    el.addEventListener('scroll', handler);

    return () => {
      el.removeEventListener('scroll', handler);
      setIndexMap({});
    };
  }, [indexEnable, list, setIndexMap]);

  const content = useMemo(() => {
    return list.map((matched, i) => (
      <PhraseItem
        ref={(el) => {
          itemsRef.current[i] = el;
        }}
        index={indexMap[i]}
        active={i === selectIndex}
        data={matched}
        mercadoQAmode={mercadoQAmode}
        mode={mode}
        onSendClick={handleSendClick}
        onEditClick={handleEditClick}
        onMouseMove={() => {
          if (selectIndex === i) return;
          setSelectIndex(i);
          onPhraseSelected?.(list[i]);
        }}
        onMouseLeave={() => {
          setSelectIndex(-1);
          onPhraseSelected?.(undefined);
        }}
      />
    ));
  }, [
    indexMap,
    list,
    mercadoQAmode,
    selectIndex,
    mode,
    handleSendClick,
    setSelectIndex,
    onPhraseSelected,
    handleEditClick,
  ]);

  useKeyboardHandler(
    ['ArrowDown', 'ArrowUp', 'Enter', 'Space'],
    (e: KeyboardEvent) => {
      if (e.code === 'ArrowDown') {
        let newVal = selectIndex + 1;
        newVal = newVal < list.length ? newVal : list.length - 1;
        setSelectIndex(newVal);
        scrollTo(newVal);
        onPhraseSelected?.(list[newVal]);
        return false;
      }
      if (e.code === 'ArrowUp') {
        let newVal = selectIndex - 1;
        newVal = newVal >= 0 ? newVal : 0;
        setSelectIndex(newVal);
        scrollTo(newVal);
        onPhraseSelected?.(list[newVal]);
        return false;
      }
      if (e.code === 'Enter') {
        if (list?.[selectIndex]) {
          setInput('');
          onPressEnter?.(list[selectIndex]);
        }
        return false;
      }
      if (e.code === 'Space') {
        if (list?.[selectIndex] && !mercadoQAmode) {
          const { text, onlyText } = getPhraseTextElement(list[selectIndex]);
          if (!onlyText) {
            message.warning(t('scene.chatWorkspace.quickReply.hint.editNotSupported'));
            return;
          }

          if (text) {
            setSelectIndex(-1);
            onFillPhraseText(getFillPhraseText(text, mode !== 'onlyFianl'));
          }
        }
        return false;
      }

      return true;
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.TOOLBAR,
      preventDefault: true,
    },
  );

  return (
    <PopoverContent style={{ width: width || 520 }}>
      <div ref={listRef}>{content}</div>
      <footer>
        <PhraseToolbar mercadoQAmode={mercadoQAmode} />
      </footer>
    </PopoverContent>
  );
}

function Implement(props: ImplementProps) {
  const {
    width,
    phraseList,
    region,
    visible: visibleProp = true,
    mercadoQAmode,
    conversationToggle = true,
    input,
    setInput,
    onPhraseSelected,
    onPressEnter,
    onFillPhraseText,
    onContentClick,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const [_visible, setVisible] = useState(false);
  const visible = _visible && visibleProp;
  const [selectIndex, setSelectIndex] = useState(-1);

  const [matchedPhraseList, setMatchedPhraseList] = useState<PhraseValue[]>([]);

  const searchPhrase = useSearchPhraseV2();
  const setConversationToggle = useSetConversationToggle('passivePhraseHide');

  useEffect(() => {
    if (conversationToggle) {
      setConversationToggle(visible ? false : true);
    }
  }, [visible, conversationToggle, setConversationToggle]);

  const { mode } = usePhraseDisplayMode();

  const [indexMap, setIndexMap] = useState<Record<number, number>>({});

  useDebounceEffect(
    () => {
      if (!input || !input.trim()) {
        setVisible(false);
        return;
      }

      const _matchedPhraseList = sortPhraseList(searchPhrase(input, phraseList, false))?.slice(
        0,
        countLimit,
      );

      if (!mercadoQAmode && !_matchedPhraseList?.length && input.startsWith('/')) {
        const index = parseInt(input[input.length - 1]);
        const invertMap = invert(indexMap) as any;
        const invertIndex = invertMap[index];
        const matchedPhrase = matchedPhraseList[invertIndex];
        if (matchedPhrase) {
          const { text, onlyText } = getPhraseTextElement(matchedPhrase);
          if (!onlyText) {
            message.warning(t('scene.chatWorkspace.quickReply.hint.editNotSupported'));
            return;
          }
          if (text) {
            setSelectIndex(-1);
            onFillPhraseText(getFillPhraseText(text, mode !== 'onlyFianl'));
          }
        }
      }

      setMatchedPhraseList(_matchedPhraseList || []);

      const visibleValue = !!(_matchedPhraseList && _matchedPhraseList.length > 0);
      setVisible(visibleValue);
      if (visibleValue) {
        setSelectIndex(-1);
      }
    },
    [input, phraseList],
    { wait: 500 },
  );

  return (
    <Popover
      visible={visible}
      content={
        visible ? (
          <div onClick={onContentClick}>
            <MatchedPhraseList
              indexEnable={input.startsWith('/')}
              width={width}
              list={matchedPhraseList}
              region={region}
              mercadoQAmode={mercadoQAmode}
              selectIndex={selectIndex}
              indexMap={indexMap}
              setIndexMap={setIndexMap}
              onPhraseSelected={onPhraseSelected}
              onPressEnter={onPressEnter}
              setInput={setInput}
              setSelectIndex={setSelectIndex}
              onFillPhraseText={onFillPhraseText}
            />
          </div>
        ) : (
          <></>
        )
      }
      placement="topLeft"
      arrowContent={<div />}
      {...otherProps}
    />
  );
}

export default NewPassivePopover;
